/* eslint-disable max-len */
import { DEFAULT_STATE, PRICE_RANGE, SUBSCRIPTION_TIER } from '../constants'
import { searchPageStore } from '../index'

/**
 * Gets the current priceRanges value, always returns an array
 * @returns {string[]} - Array of price ranges, defaults to ["free"] if empty/undefined
 */
const getCurrentPriceRanges = () => {
  const state = searchPageStore.getState()
  const { priceRanges } = state

  if (!Array.isArray(priceRanges)) {
    return []
  }

  return priceRanges
}

// Free and every paid option are mutually exclusive (Edvardas, 2026-10-05): picking one clears the
// other, in both directions.
const FREE_CLEARS_PAID = {
  onSale: false,
  withSubscription: false,
  subscriptionTiers: DEFAULT_STATE.subscriptionTiers,
  minPrice: DEFAULT_STATE.minPrice,
  maxPrice: DEFAULT_STATE.maxPrice,
}

const withoutFree = () => getCurrentPriceRanges().filter((p) => p !== PRICE_RANGE.FREE)

/**
 * Sets the onSale filter. Turning it on clears Free.
 * @param {boolean} onSale - Whether to filter for items on sale
 */
export const setOnSale = (onSale) => {
  if (onSale) {
    searchPageStore.setState({ onSale, priceRanges: withoutFree() })
    return
  }

  searchPageStore.setState({ onSale })
}

/**
 * Sets the withSubscription filter
 * @param {boolean} withSubscription - Whether to filter for items with a subscription
 */
export const setWithSubscription = (withSubscription) => {
  if (withSubscription) {
    searchPageStore.setState({ withSubscription, priceRanges: withoutFree() })
    return
  }

  searchPageStore.setState({ withSubscription, subscriptionTiers: DEFAULT_STATE.subscriptionTiers })
}

/**
 * Ticks or unticks one subscription plan. The subscription filter stays on while any plan is
 * ticked; ticking one clears Free.
 * @param {string} tier - One of SUBSCRIPTION_TIER
 * @param {boolean} isOn - Whether the plan was ticked
 */
export const toggleSubscriptionTier = (tier, isOn) => {
  const { withSubscription, subscriptionTiers = [] } = searchPageStore.getState()
  // A subscription search with no plan picked (an old "All" URL, the plain checkbox) spans every
  // tier, which is what Premium shows.
  const current = withSubscription && subscriptionTiers.length === 0
    ? [SUBSCRIPTION_TIER.PREMIUM]
    : subscriptionTiers
  const next = isOn ? [...new Set([...current, tier])] : current.filter((t) => t !== tier)

  if (next.length === 0) {
    searchPageStore.setState({ withSubscription: false, subscriptionTiers: DEFAULT_STATE.subscriptionTiers })
    return
  }

  searchPageStore.setState({ withSubscription: true, subscriptionTiers: next, priceRanges: withoutFree() })
}

/**
 * Turns the subscription filter on with exactly one plan ticked, replacing any other pick
 * (including the implicit every-tier search). Clears Free.
 * @param {string} tier - One of SUBSCRIPTION_TIER
 */
export const selectSubscriptionTier = (tier) => {
  searchPageStore.setState({ withSubscription: true, subscriptionTiers: [tier], priceRanges: withoutFree() })
}

/**
 * Sets the price ranges filter. Quick-select buckets and the custom min/max range override each
 * other, so any non-empty selection resets minPrice/maxPrice.
 * @param {string[]} priceRanges - Array with price range values
 */
export const setPriceRanges = (priceRanges) => {
  if (priceRanges.includes(PRICE_RANGE.FREE)) {
    searchPageStore.setState({ priceRanges: [PRICE_RANGE.FREE], ...FREE_CLEARS_PAID })
    return
  }

  if (priceRanges.length === 0) {
    searchPageStore.setState({ priceRanges })
    return
  }

  searchPageStore.setState({
    priceRanges,
    minPrice: DEFAULT_STATE.minPrice,
    maxPrice: DEFAULT_STATE.maxPrice,
  })
}

/**
 * Adds a price range to the array and resets the custom min/max range
 * @param {string} priceRange - Price range to add
 */
export const addPriceRange = (priceRange) => {
  if (priceRange === PRICE_RANGE.FREE) {
    searchPageStore.setState({ priceRanges: [PRICE_RANGE.FREE], ...FREE_CLEARS_PAID })
    return
  }

  const currentPriceRanges = getCurrentPriceRanges()

  if (currentPriceRanges.length === 1 && currentPriceRanges[0] === PRICE_RANGE.FREE) {
    searchPageStore.setState({
      priceRanges: [priceRange],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
    })
    return
  }

  if (!currentPriceRanges.includes(priceRange)) {
    searchPageStore.setState({
      priceRanges: [...currentPriceRanges, priceRange],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
    })
  }
}

/**
 * Removes a price range from the array
 * @param {string} priceRange - Price range to remove
 */
export const removePriceRange = (priceRange) => {
  const currentPriceRanges = getCurrentPriceRanges()

  const newPriceRanges = currentPriceRanges.filter((p) => p !== priceRange)
  searchPageStore.setState({ priceRanges: newPriceRanges })
}

/**
 * Sets the minimum price filter. A typed value clears the quick-select buckets; clearing the
 * input leaves them untouched.
 * @param {number|string} minPrice - Minimum price value or empty string
 */
export const setMinPrice = (minPrice) => {
  if (minPrice === DEFAULT_STATE.minPrice) {
    searchPageStore.setState({ minPrice })
    return
  }

  searchPageStore.setState({ minPrice, priceRanges: DEFAULT_STATE.priceRanges })
}

/**
 * Sets the maximum price filter. A typed value clears the quick-select buckets; clearing the
 * input leaves them untouched.
 * @param {number|string} maxPrice - Maximum price value or empty string
 */
export const setMaxPrice = (maxPrice) => {
  if (maxPrice === DEFAULT_STATE.maxPrice) {
    searchPageStore.setState({ maxPrice })
    return
  }

  searchPageStore.setState({ maxPrice, priceRanges: DEFAULT_STATE.priceRanges })
}

/**
 * Clears all pricing filters
 */
export const clearPricing = () => {
  searchPageStore.setState({
    onSale: DEFAULT_STATE.onSale,
    withSubscription: DEFAULT_STATE.withSubscription,
    subscriptionTiers: DEFAULT_STATE.subscriptionTiers,
    priceRanges: DEFAULT_STATE.priceRanges,
    minPrice: DEFAULT_STATE.minPrice,
    maxPrice: DEFAULT_STATE.maxPrice,
  })
}
